// =============================================================================
// TakeOnLock.jsx
// =============================================================================
// The staff "Take-On" gate shown above a request. A staff/responder must claim
// (take on) a request before they can review, approve, or comment on it.
//
// Renders one of:
//   - a "Loading..." line while the take-on lock is being checked, or
//   - the lock card (request title + a "Take On This Request" button) when the
//     request has not yet been taken on.
//
// When nothing should show (not staff, or already taken on) it renders null, so
// callers can drop it in unconditionally — it owns its own gating.
//
// Pulled out of RequestDetail.jsx (decomposition tidy-up). Lock state itself is
// owned by useTaskLock in RequestDetail; this is purely the presentation.
//
// Props
// ─────
//   isStaff          — only staff/responders see the lock
//   taskLock         — the active uncompleted request_tasks row (truthy = locked)
//   taskLockLoading  — true while the lock is being loaded
//   takingOn         — true while a take-on is in flight (disables the button)
//   handleTakeOn     — handler invoked when the button is clicked
//   req              — request object (read for refNo / shelterName / shelterCode / state)
// =============================================================================
import React from "react";
import { GLYPH } from "../../utils/glyphs";
import { formatRef } from "../../utils/helpers";

export function TakeOnLock({ isStaff, taskLock, taskLockLoading, takingOn, handleTakeOn, req }) {
  if (!isStaff) return null;
  return (
    <React.Fragment>
      {/* Take-On lock: staff must claim the request before working on it */}
      {taskLockLoading && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#607D79", fontSize: 14 }}>
          Loading...
        </div>
      )}
      {taskLock && !taskLockLoading && (
        <div>
          <h1 style={{ margin: "0 0 16px", fontSize: 22, color: "#1A2E2B" }}>
            {`Request ${formatRef(req.refNo)}`}
          </h1>
          <div
            style={{
              background: "#FFF8E1",
              border: "2px solid #FFB300",
              borderRadius: 12,
              padding: "28px 32px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 22, marginBottom: 8 }}>{GLYPH.lock}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#E65100", marginBottom: 6 }}>
              This request hasn't been assigned to anyone yet
            </div>
            <div style={{ fontSize: 13, color: "#795548", marginBottom: 6 }}>
              {req.shelterName || ""}
              {req.shelterCode ? ` (${req.shelterCode})` : ""}
              {req.state ? ` — ${req.state}` : ""}
            </div>
            <div style={{ fontSize: 13, color: "#795548", marginBottom: 16 }}>
              You must assign this request to yourself before you can review, approve, or comment on
              it.
            </div>
            <button
              onClick={handleTakeOn}
              disabled={takingOn}
              style={{
                background: "#2E7D32",
                color: "white",
                border: "none",
                borderRadius: 8,
                padding: "12px 32px",
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
                opacity: takingOn ? 0.6 : 1,
              }}
            >
              {takingOn ? "Assigning..." : "Assign this request to me"}
            </button>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}
